<script setup lang="ts">
import type { ProductVariant } from 'types/models/product'
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from 'reka-ui'

interface Props {
  variants: ProductVariant[]
  selected?: ProductVariant
  forceOpen: boolean
  isPrimeProduct: boolean
}

const props = withDefaults(defineProps<Props>(), {
  selected: undefined,
})

const emit = defineEmits(['selectionClosed', 'changeVariant'])
const gtm = useGTM()
const { $t } = useNuxtApp()

const selectedVariantSize = ref()
const isVariantsExpanded = ref(false)

onMounted(() => {
  clearVariantSelection()
})

watch(
  () => props.variants,
  () => {
    clearVariantSelection()
  },
)

watch(
  () => props.forceOpen,
  () => {
    if (props.forceOpen)
      isVariantsExpanded.value = true
  },
)

function clearVariantSelection() {
  selectedVariantSize.value = ''
  if (props.selected)
    selectedVariantSize.value = props.selected.size
}

function changeVariant(): void {
  if (selectedVariantSize.value) {
    const selectedVariant = props.variants.find(item => item.size === selectedVariantSize.value)
    emit('changeVariant', selectedVariant)
    gtm.pushProductPageEvent({ action: 'sizeSelection', label: selectedVariantSize.value })
    hideVariantsList()
  }
}

function toggleOpen() {
  isVariantsExpanded.value = !isVariantsExpanded.value

  if (isVariantsExpanded.value === false)
    emit('selectionClosed')
}

function hideVariantsList() {
  isVariantsExpanded.value = false
  emit('selectionClosed')
}
</script>

<template>
  <SelectRoot
    v-model="selectedVariantSize"
    :open="isVariantsExpanded"
    @update:model-value="changeVariant"
    @update:open="toggleOpen"
  >
    <SelectTrigger
      class="SelectTrigger"
      aria-label="Select size"
      data-test="size-select-trigger"
      :disabled="variants.length === 1"
      :class="[{ Selected: selectedVariantSize }]"
    >
      <SelectValue
        :placeholder="$t('sizeSelectorTag')"
      />
    </SelectTrigger>

    <SelectPortal>
      <SelectContent
        class="SelectContent"
        position="popper"
      >
        <SelectViewport>
          <SelectGroup data-test="product-size-selector">
            <SelectItem
              v-for="(variant) in variants"
              :key="variant.id"
              data-test="size-option"
              class="SelectItem"
              :value="variant.size"
              :disabled="!isPrimeProduct && variant.stockStatus === 'no-stock'"
              :class="[{ disabled: !isPrimeProduct && variant.stockStatus === 'no-stock' }]"
            >
              <SelectItemText
                :aria-label="`Size ${variant.size}`"
                as-child
              >
                <span :class="[{ SelectedTextDisabled: !isPrimeProduct && variant.stockStatus === 'no-stock' }]">
                  {{ variant.size }}
                </span>
                <span v-if="variant.stockStatus === 'low-stock'"> {{ $t('fewLeft') }} </span>
                <span v-else-if="!isPrimeProduct && variant.stockStatus === 'no-stock'"> {{ $t('outOfStock') }} </span>
              </SelectItemText>
            </SelectItem>
          </SelectGroup>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';

/* reset */
button {
  all: unset;
}

.SelectTrigger {
  padding: 0 15px;
  gap: 5px;
  box-shadow: 0 2px 10px var(--black-a7);
  height: 4rem;
  display: grid;
  grid-template: 'select-text select-icon'/1fr 3rem;
  background-color: var(--white);
  border-radius: 2px;
  align-items: center;
  cursor: pointer;

  &::after {
    content: '';
    height: 40%;
    width: 100%;
    grid-area: select-icon;
    transform: rotate(-90deg);
    background: url('/icons/left-arrow.svg') center center no-repeat;
  }

  &[data-placeholder] {
    color: var(--gray-inactive);
  }
}

.Selected {
  background-color: var(--yellow);
}

.SelectTrigger[data-disabled]{
  background-color: var(--yellow);
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;

  &::after {
    display: none
  }

  span {
    padding-left: 0;
  }
}

.SelectItem {
  position: relative;
  border-radius: 0;
  cursor: pointer;
  background-color: var(--white);
  width: 100%;
  min-width: 15rem;
  font-size: 1.4rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 4rem;
  padding: 0 35px 0 25px;
  user-select: none;

  &[data-disabled] {
    background-color: var(--gray);
    pointer-events: none;
  }

  &[data-highlighted] {
    outline: none;
    background-color: var(--yellow);
  }
}
</style>
